import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { WhatsappLogo, X, CaretLeft, CaretRight, MagnifyingGlassPlus } from '@phosphor-icons/react';
import { BRAND } from '@/theme/brand';
import { useVehicles, vehicleDisplayImages } from '@/hooks/useVehicles';

const fleetBadge = (model, index) => {
  const m = (model || '').toLowerCase();
  if (m.includes('dzire') || m.includes('swift')) return 'Popular';
  if (m.includes('innova')) return 'Premium';
  if (m.includes('traveller') || m.includes('tempo') || m.includes('force')) return 'Group';
  if (index === 0) return 'Popular';
  return null;
};

const ImageLightbox = ({ open, images, index, title, onClose, onChangeIndex }) => {
  const goPrev = useCallback(() => {
    onChangeIndex((index - 1 + images.length) % images.length);
  }, [index, images.length, onChangeIndex]);

  const goNext = useCallback(() => {
    onChangeIndex((index + 1) % images.length);
  }, [index, images.length, onChangeIndex]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && images.length > 1) goPrev();
      if (e.key === 'ArrowRight' && images.length > 1) goNext();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, images.length, onClose, goPrev, goNext]);

  if (!open || typeof document === 'undefined') return null;

  const hasMultiple = images.length > 1;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/65"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} photos`}
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div className="min-w-0 pr-2">
            <p className="font-body font-semibold text-[#1B2D5E] text-sm sm:text-base truncate">{title}</p>
            {hasMultiple && (
              <p className="font-body text-xs text-gray-500 mt-0.5">
                Photo {index + 1} of {images.length}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-[#1B2D5E] transition-colors"
            aria-label="Close preview"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div
          className="relative w-full bg-gradient-to-br from-[#EEF2FF] to-[#F8F4EE]"
          style={{ height: 'min(50dvh, 340px)' }}
        >
          <div className="absolute inset-0 flex items-center justify-center px-12 sm:px-14 py-4">
            <img
              src={images[index]}
              alt={title}
              className="block max-w-full max-h-full w-auto h-auto object-contain"
            />
          </div>

          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white text-[#1B2D5E] shadow-md hover:bg-gray-50 transition-colors"
                aria-label="Previous photo"
              >
                <CaretLeft size={20} weight="bold" />
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white text-[#1B2D5E] shadow-md hover:bg-gray-50 transition-colors"
                aria-label="Next photo"
              >
                <CaretRight size={20} weight="bold" />
              </button>
            </>
          )}
        </div>

        {hasMultiple && (
          <div className="flex items-center justify-center gap-2 px-3 py-3 border-t border-gray-100 overflow-x-auto">
            {images.map((src, i) => (
              <button
                key={`${title}-lb-${i}`}
                type="button"
                onClick={() => onChangeIndex(i)}
                className={`shrink-0 w-12 h-10 rounded-md overflow-hidden border-2 transition-all ${
                  i === index
                    ? 'border-[#E8651A] ring-1 ring-[#E8651A]/30'
                    : 'border-gray-200 opacity-70 hover:opacity-100'
                }`}
                aria-label={`Show photo ${i + 1}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

const FleetCard = ({ v, index, wa, onBook }) => {
  const images = vehicleDisplayImages(v);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const badge = fleetBadge(v.model, index);
  const hasGallery = images.length > 1;

  const openLightbox = (i) => {
    setActiveIndex(i);
    setLightboxOpen(true);
  };

  return (
    <>
      <div className="w-full rounded-2xl border border-gray-100 overflow-hidden bg-white transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(27,45,94,0.10)] group">
        <div className="bg-gradient-to-br from-[#EEF2FF] to-[#F8F4EE] rounded-t-2xl p-5 flex flex-col items-center justify-center min-h-[160px] relative">
          {badge && (
            <span className="absolute top-2.5 right-2.5 bg-[#1B2D5E] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full z-10">
              {badge}
            </span>
          )}
          <button
            type="button"
            onClick={() => openLightbox(activeIndex)}
            className="relative group/preview cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8651A] rounded-lg"
            aria-label={`View larger photo of ${v.model}`}
          >
            <img
              src={images[activeIndex]}
              alt={v.model}
              className="max-h-[120px] w-auto object-contain transition-transform duration-200 group-hover/preview:scale-105"
            />
            <span className="absolute bottom-1 right-1 flex items-center gap-0.5 bg-black/55 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full opacity-0 group-hover/preview:opacity-100 transition-opacity">
              <MagnifyingGlassPlus size={10} weight="bold" />
              Expand
            </span>
          </button>
          {hasGallery && (
            <div className="mt-3 flex items-center gap-1.5 flex-wrap justify-center px-2">
              {images.map((src, i) => (
                <button
                  key={`${v.id}-thumb-${i}`}
                  type="button"
                  onClick={() => openLightbox(i)}
                  className={`w-10 h-8 rounded-md overflow-hidden border-2 transition-all cursor-zoom-in ${
                    i === activeIndex
                      ? 'border-[#E8651A] ring-1 ring-[#E8651A]/40'
                      : 'border-white/80 opacity-75 hover:opacity-100'
                  }`}
                  aria-label={`View photo ${i + 1} of ${v.model}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 text-center border-b border-gray-100">
          <h3 className="font-body font-semibold text-[#1B2D5E]" style={{ fontSize: '15px' }}>
            {v.model}
          </h3>
          <p className="text-xs text-gray-500 font-body mt-0.5">
            {v.capacity}+1 Seater{v.ac !== false ? ' · ✓ AC' : ''}
          </p>
          <p className="mt-1">
            <span className="text-2xl font-extrabold text-[#E8651A] font-body">₹{v.rate_per_km}</span>
            <span className="text-xs text-gray-400 font-normal"> /km</span>
          </p>
        </div>
        <div className="p-4 pt-3 flex flex-col gap-2">
          <a
            href={`https://wa.me/${wa}?text=${encodeURIComponent(`Book ${v.model}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 bg-[#25D366] text-white rounded-lg text-xs font-bold py-2 w-full font-body hover:bg-[#20BD5A] transition-colors"
          >
            <WhatsappLogo size={14} weight="fill" />
            WhatsApp
          </a>
          <button
            type="button"
            onClick={onBook}
            className="w-full bg-[#1B2D5E] hover:bg-[#E8651A] text-white font-bold py-2.5 rounded-xl transition-colors duration-200 text-[12px] font-body"
          >
            Book Now
          </button>
        </div>
      </div>

      <ImageLightbox
        open={lightboxOpen}
        images={images}
        index={activeIndex}
        title={v.model}
        onClose={() => setLightboxOpen(false)}
        onChangeIndex={setActiveIndex}
      />
    </>
  );
};

const AtithiFleetSection = () => {
  const navigate = useNavigate();
  const wa = BRAND.contact.phone;
  const { vehicles, loading } = useVehicles({ activeOnly: true });

  return (
    <section id="fleet" className="section-outer pt-16 pb-8 bg-[#F8F4EE]" data-testid="vehicles-section" data-aos="fade-up">
      <div className="section-inner !py-0 !pb-10">
        <div className="text-center mb-8">
          <p className="section-label">OUR FLEET</p>
          <h2 className="section-heading mt-2">Popular Cabs</h2>
          <p className="section-subtext mt-3">Choose fleets according to your requirements</p>
        </div>

        {loading && vehicles.length === 0 ? (
          <p className="text-center text-gray-500 font-body text-sm">Loading fleet...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center [&>*:last-child:nth-child(3n-2)]:lg:col-start-2">
            {vehicles.map((v, index) => (
              <FleetCard
                key={v.id}
                v={v}
                index={index}
                wa={wa}
                onBook={() => navigate('/book-vehicle?trip=one_way')}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AtithiFleetSection;
